import React, { useMemo, useContext } from 'react';
import classNames from 'classnames';
import { Field, withTypes } from 'react-final-form';
import InputField from 'components/InputField';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import validate from '../../../../validations';
import useCallbackAsync from 'hooks/useCallbackAsync';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import XIcon from 'icons/X.icon';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import { getUserFullName } from 'utils';

import './ReferAFriend.styles.scss';
import './ReferAFriend.styles.responsive.scss';

export type ReferFriendFormType = {
  inviterName: string;
  inviterEmail: string;
  inviteeName: string;
  inviteeEmail: string;
};

type ReferAFriendProps = {
  className?: string;
  onClose: () => void;
};

const ReferAFriend: React.FC<ReferAFriendProps> = (props) => {
  const { className, onClose } = props;

  const { t } = useTranslation();

  const { currentUser } = useContext(CurrentUserContext);
  const { popSuccess } = useContext(PopNotificationsContext);

  const classes = classNames('refer-friend', className);
  const { Form } = withTypes<ReferFriendFormType>();

  const initialValues = useMemo(() => {
    if (!currentUser) return {};

    const { firstName, lastName, email } = currentUser;

    return {
      inviterName: getUserFullName(firstName, lastName),
      inviterEmail: email,
    };
  }, [currentUser]);

  const validations = useMemo(
    () => ({
      inviterName: validate.required(
        t('requiredField2', { fieldName: t('name').toLowerCase() }),
      ),
      inviterEmail: validate.composeValidators(
        validate.required(
          t('requiredField2', {
            fieldName: t('email').toLowerCase(),
          }),
        ),
        validate.email(),
      ),

      inviteeName: validate.required(
        t('requiredField2', { fieldName: t('referringFriend').toLowerCase() }),
      ),
      inviteeEmail: validate.composeValidators(
        validate.required(
          t('requiredField2', {
            fieldName: t('referringFriendEmail'),
          }),
        ),
        validate.email(),
      ),
    }),
    [t],
  );

  const [handleOnSubmit, loading] = useCallbackAsync(
    async (values: ReferFriendFormType) => {
      await api.general.referAFriend(values);
      popSuccess(t(SUCCESS_MESSAGES.REFER_FRIEND));
      onClose();
    },
  );

  return (
    <Form
      onSubmit={handleOnSubmit}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form className={classes} onSubmit={handleSubmit}>
          <Button
            variant="icon"
            aria-label="Close"
            icon={XIcon}
            onClick={onClose}
          />

          <section className="refer-friend__refer">
            <h3 className="refer-friend__refer__title">{t('referAFriend')}</h3>
            <p className="refer-friend__refer__text">
              {t('referAFriendContent')}
            </p>
          </section>

          <section className="refer-friend__form">
            {!currentUser && (
              <div className="form-row">
                <Field
                  placeholder={t('yourName')}
                  name="inviterName"
                  component={InputField}
                  styleType="underlined"
                  validate={validations.inviterName}
                />
                <Field
                  placeholder={t('yourEmail')}
                  name="inviterEmail"
                  component={InputField}
                  styleType="underlined"
                  validate={validations.inviterEmail}
                />
              </div>
            )}

            <Field
              placeholder={t('whoToRefer')}
              name="inviteeName"
              component={InputField}
              styleType="underlined"
              validate={validations.inviteeName}
            />
            <Field
              placeholder={t('theirEmail')}
              name="inviteeEmail"
              component={InputField}
              styleType="underlined"
              validate={validations.inviteeEmail}
            />
            <Button
              className="refer-submit"
              type="submit"
              variant="outline"
              aria-label="Send referral."
            >
              {loading ? (
                <Loader inline size="small" />
              ) : (
                t('sendReferralEmail')
              )}
            </Button>
          </section>
        </form>
      )}
    />
  );
};

export default React.memo(ReferAFriend);
